import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { GetServerSideProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import unescape from "lodash/unescape";
import { CartContext } from "context/cart";
import { ZipCodeContext } from "context/zipCode";
import { ModalContext } from "context/modal";
import { AppContext } from "context/app";
import { LogContext } from "context/log";
import { UserContext } from "context/user";
import { PurifiedHtml } from "@pepdirect/v3/ui/PurifiedHtml";
import { Heading } from "@pepdirect/v3/ui/Heading";
import { ExternalLink } from "@pepdirect/v3/ui/ExternalLink";
import { Expandable } from "components/Expandable";
import { QuantityDropdown } from "components/Product/QuantityDropdown";
import { PDPImages } from "@pepdirect/v3/ui/PDPImages";
import { DeliverToZip } from "components/Product/DeliverToZip";
import { PriceChooser } from "components/Product/PriceChooser";
import { ShippingBadge } from "components/Product/ShippingBadge";
import createSsrClient from "services/graphql/ssr-client";
import {
  SearchVariantsBySlugDocument,
  SearchVariantsBySlugQuery,
  SearchVariantsBySlugQueryVariables,
  SearchProductByKeyDocument,
  SearchProductByKeyQuery,
  SearchProductByKeyQueryVariables,
  VariantFragment,
  CampaignType,
} from "services/graphql/generated";
import { logProductDetailBuyButtonClickEvent } from "@pepdirect/v3/helpers/analyticsLogger";
import { getCloudflareImageUrl } from "@pepdirect/v3/helpers/cloudflareImages";
import { useRestoreScrollOnBrowserBack } from "@pepdirect/v3/hooks/useScrollRestoration";
import {
  convertFromCentsToDollars,
  formatDollars,
  FORMAT_NO_SIGN,
} from "helpers/currency";
import { PAGE_TYPE_ENUM } from "@pepdirect/v3/types";
import { META_TITLE, CANONICAL_URL_BASE } from "constants/meta";
import { Subscription_Options } from "constants/subscription";
import { endpoints, cart } from "config";
import st from "./product.module.scss";

const { ONE_TIME, SUB_SAVE } = Subscription_Options;
const { maxCartQty: maxCartQtyDefault } = cart;

interface ProductPageProps {
  variant: VariantFragment | null;
  product: SearchProductByKeyQuery["searchProducts"]["products"][0] | null;
}

export const getServerSideProps: GetServerSideProps<ProductPageProps> = async ({
  params,
  ...rest
}) => {
  // Here we use server side GQL client
  const gqlClient = createSsrClient(rest.req);
  try {
    const { data } = await gqlClient.query<
      SearchVariantsBySlugQuery,
      SearchVariantsBySlugQueryVariables
    >({
      query: SearchVariantsBySlugDocument,
      variables: {
        slug: params?.slug as string,
        pagination: { page: 1, limit: 1 },
      },
    });
    const variant = data?.searchVariants?.variants?.[0];

    if (!variant) {
      return {
        notFound: true,
      };
    }

    // Fetch the full product with all variants using the product key
    const { data: productData } = await gqlClient.query<
      SearchProductByKeyQuery,
      SearchProductByKeyQueryVariables
    >({
      query: SearchProductByKeyDocument,
      variables: {
        key: variant.product.key,
        pagination: { page: 1, limit: 1 },
      },
    });

    const product = productData?.searchProducts?.products?.[0] || null;

    return {
      props: {
        variant,
        product,
      },
    };
  } catch (e) {
    return {
      notFound: true,
    };
  }
};

const NA = "N/A";

/**
 * Variant is the Item Variant the user is viewing.
 * Product is the full Product (a grouping of variants) the variant belongs to.
 */
const ProductPage: FC<ProductPageProps> = ({ variant, product }) => {
  const { currentUserId } = useContext(UserContext);
  const { cart, updateCartItem } = useContext(CartContext);
  const { tenant } = useContext(AppContext);
  const { blockedItems } = useContext(ZipCodeContext);
  const { open } = useContext(ModalContext);
  const { useLogPageView } = useContext(LogContext);
  const router = useRouter();
  useRestoreScrollOnBrowserBack(router);

  const variantGtin = variant?.gtin || "";
  const description = variant?.webData?.description || "";
  const itemDesignerArtistBrandName =
    variant?.webData?.designerArtistBrandName || "";
  const itemDimensions = variant?.webData?.dimensions || "";
  const material = variant?.webData?.material || "";
  const returnsPolicy = variant?.webData?.returnsPolicy || "";
  const subscriptionPrice = variant?.subscriptionPrice || 0;
  const discounts = variant?.discounts || [];
  const discount = discounts.find((d) => d?.type === CampaignType.Subscription);
  const isSubscribable = variant?.subscribable || false;

  const [subscriptionOption, setSubscriptionOption] =
    useState<Subscription_Options>(ONE_TIME);

  // Find cart item that matches both variant AND subscription status
  const cartItem = useMemo(() => {
    return cart?.variantItems?.find((item) => {
      const matchesVariant = item?.variant.gtin === variantGtin;
      const matchesSubscription =
        subscriptionOption === SUB_SAVE
          ? !!item?.subscriptionIntervalInDays
          : !item?.subscriptionIntervalInDays;
      return matchesVariant && matchesSubscription;
    });
  }, [cart?.variantItems, variantGtin, subscriptionOption]);

  const quantity = cartItem?.quantity || 0;

  // Initialize subscription option from cart item on mount
  useEffect(() => {
    const existingCartItem = cart?.variantItems?.find(
      (item) => item?.variant.gtin === variantGtin
    );
    if (existingCartItem?.subscriptionIntervalInDays) {
      setSubscriptionOption(SUB_SAVE);
    }
  }, [cart?.variantItems, variantGtin]);

  const formattedSubscriptionPrice =
    (subscriptionPrice && convertFromCentsToDollars(subscriptionPrice)) || null;
  const intervals = useMemo(() => {
    const intervalsVal =
      tenant?.shoppingBagConfig?.availableSubscriptionIntervalsInDays || [];
    return intervalsVal;
  }, [tenant]);
  const showPriceChooser =
    isSubscribable && tenant?.shoppingBagConfig?.orderSubscriptionsEnabled;
  const [defaultInterval] = intervals;

  const [interval, setInterval] = useState(
    cartItem?.subscriptionIntervalInDays || defaultInterval
  );

  useEffect(() => {
    const [firstInterval] = intervals;
    setInterval(cartItem?.subscriptionIntervalInDays || firstInterval);
  }, [cartItem?.subscriptionIntervalInDays, intervals]);

  const maxCartQty =
    variant?.maximumPurchaseQuantity ||
    tenant?.shoppingBagConfig?.maxAmountForSelect ||
    maxCartQtyDefault;
  const isItemUnavailable = blockedItems.includes(variantGtin);
  const openZipCodeModal = () => open("ZIPCODE_MODAL");

  const metaTitle = `${variant?.title} – by ${itemDesignerArtistBrandName} – ${META_TITLE}`;
  const metaUrl = `${CANONICAL_URL_BASE}/product/${variant?.slug}`;
  // TODO: move to a helper?
  const metaDescription = unescape(description || "")
    .replace("<br />", " ")
    .replace(/(&nbsp;|<([^>]+)>)/gi, "")
    .slice(0, 155)
    .concat("...");
  const metaImage = getCloudflareImageUrl(
    endpoints.productsImgCdn,
    variant?.imgUrls?.[0] || "",
    { width: 600 }
  );
  const metaPrice = formatDollars(variant?.price || 0, FORMAT_NO_SIGN);
  const price = convertFromCentsToDollars(variant?.price || 0);

  const { collection } = (router?.query || {}) as {
    collection: string | undefined;
  };
  const pageCategory = collection;
  useLogPageView(
    {
      pageType: PAGE_TYPE_ENUM.product,
      pageTitle: variant?.title || "",
      pageCategory: pageCategory || undefined,
      id: variantGtin,
    },
    variant
  );

  return (
    <div className={st.wrapper}>
      <Head>
        <title>{metaTitle}</title>
        <meta name="og:title" content={metaTitle} key="og:title" />
        <link rel="canonical" href={metaUrl} />
        <meta name="og:url" content={metaUrl} key="og:url" />
        <meta name="description" content={metaDescription} key="description" />
        <meta
          name="og:description"
          content={metaDescription}
          key="og:description"
        />
        <meta name="og:image" content={metaImage} key="og:image" />
        <meta name="og:price:amount" content={metaPrice} />
        <meta name="og:type" content="product" key="og:type" />
      </Head>
      <div className={st.itemWrapper}>
        <div className={st.itemImages}>
          <PDPImages
            image={variant?.imgUrls?.[0] || ""}
            images={
              variant?.imgUrls && variant.imgUrls.filter(Boolean).length > 1
                ? variant.imgUrls
                    .filter(Boolean)
                    .map((src, pos) => ({ pos, src }))
                : undefined
            }
            alt={`${variant?.title} photo`}
            key={`images_${variantGtin}`}
          />
        </div>
        <div className={st.itemData}>
          <div className={st.itemActionsWrapper}>
            <div className={st.title}>
              <Heading level="h1" size="xl">
                {variant?.title}
              </Heading>
            </div>
            {isItemUnavailable ? (
              <>
                <h2 className={st.unavailableHeader}>Currently unavailable</h2>
                <p className={st.unavailableText}>
                  This product cannot be delivered to your area at this time.
                  Please{" "}
                  <span
                    className={st.modalLink}
                    role="button"
                    onClick={openZipCodeModal}
                  >
                    update your delivery location
                  </span>{" "}
                  to check availability.
                </p>
              </>
            ) : (
              <>
                {showPriceChooser && interval ? (
                  <PriceChooser
                    discount={{
                      id: discount?.id,
                      activeFrom: discount?.activeFrom,
                      amount: discount?.amount || 0,
                      isPercent: discount?.isPercent || false,
                    }}
                    intervals={intervals}
                    onIntervalChange={setInterval}
                    onSubscriptionOptionChange={setSubscriptionOption}
                    price={price}
                    selectedInterval={interval}
                    subscriptionOption={subscriptionOption}
                    subscriptionPrice={formattedSubscriptionPrice}
                  />
                ) : (
                  <span className={st.price}>Our price {price}</span>
                )}
                <QuantityDropdown
                  quantity={quantity}
                  onChange={async (quantity: number) => {
                    logProductDetailBuyButtonClickEvent(
                      currentUserId,
                      variant,
                      quantity
                    );
                    return updateCartItem({
                      variantGtin,
                      quantity,
                      subscriptionIntervalInDays:
                        subscriptionOption === SUB_SAVE && interval
                          ? interval
                          : 0,
                    });
                  }}
                  outOfStock={!!variant?.isOutOfStock}
                  inventoryQty={variant?.inventoryQty || 0}
                  minQuantity={variant?.minQuantity || 0}
                  maxCartQty={maxCartQty}
                  key={`quantity_${variantGtin}`}
                />
              </>
            )}
            <DeliverToZip isSubscription={!!(showPriceChooser && interval)} />
          </div>
          <div className={st.description}>
            <h2 className={st.descriptionTitle}>Overview</h2>
            <PurifiedHtml html={description || ""} />
          </div>
          <Expandable
            className={st.expandable}
            title="Details"
            key={`expandable_${variantGtin}`}
          >
            <div className={st.expandableContent}>
              {/* TODO Repurposed / Hacked the returns policy to display additional item details; should properly implement this feature */}
              <PurifiedHtml html={returnsPolicy || ""} />
              <p>
                <strong>Brand:</strong> {itemDesignerArtistBrandName || NA}
              </p>
              <p>
                <strong>Material:</strong> {material || NA}
              </p>
              <p>
                <strong>Size/Weight:</strong> {itemDimensions || NA}
              </p>
            </div>
          </Expandable>
          <div className={st.returns}>
            <h2 className={st.descriptionTitle}>Shipping and Returns</h2>
            <div>
              <ShippingBadge />
              <p>
                Delivery times may vary, especially during peak periods.
                Unfortunately, we don’t currently ship to Alaska or Hawaii.
              </p>
            </div>
            <div>
              <h3>Returns and Issues:</h3>
              <ul>
                <li>
                  All sales are final, we do not offer any refunds or returns.
                  Please{" "}
                  <ExternalLink href="https://contact.pepsico.com/pantryshop">
                    contact us
                  </ExternalLink>{" "}
                  to let us know if you have any issues or concerns with your
                  Pantry Shop order.
                </li>
              </ul>
            </div>
          </div>
          {/* TODO: Do we need nutrition data? */}
        </div>
      </div>
      {/* TODO: PGS-2035 Reimplement recommended products once the BE supports it.  */}
      {/* <Recommended
        products={product?.recommendedItems || []}
        key={`recommended_${product?._id}`}
      /> */}
    </div>
  );
};

export default ProductPage;
